/**
 * E-GTCC-VIEW-DPLANE1 — tab "Monitoring & Alerts (DB)".
 *
 * The DB monitoring view of the old console, fed by the read-only
 * monitoring endpoints `/v1/admin/data-plane/monitoring/{summary,vps-fleet}`.
 *
 * "DB" is not guessed: the rule table keeps exactly the rule groups whose
 * name contains "postgres", and the alert table keeps exactly the alerts
 * whose `alertname` is one of those rules' names. An alert the monitoring
 * stack never attributed to a PostgreSQL rule is therefore never shown here
 * as a database alert.
 */
import type { ReactNode } from "react";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";

import type {
  MonitoringAlertDto,
  MonitoringRuleDto,
  MonitoringSeverity,
  MonitoringSummaryDto,
  VpsFleetDto,
  VpsFleetHostDto,
  VpsState,
} from "../../../../monitoring/types.js";
import {
  fetchDplaneMonitoringSummary,
  fetchDplaneVpsFleet,
  DPLANE_MONITORING_SUMMARY_KEY,
  DPLANE_VPS_FLEET_KEY,
} from "../../../api/dplane.js";
import { formatWholePercent, type FleetTone } from "../../cockpit/components/FleetPrimitives.js";
import { Dot, Row } from "../../../ui/primitives.js";
import {
  asList,
  DplaneGate,
  DplanePanel,
  DPLANE_QUERY_OPTIONS,
  GRID_COLUMN_STYLE,
  HOST_STYLE,
  MONO_STYLE,
  Mono,
  TABLE_CELL_STYLE,
  TABLE_HEAD_STYLE,
  TABLE_STYLE,
  TABLE_WRAP_STYLE,
  type DplaneTabProps,
} from "../components/DplanePanels.js";

/** The group filter — the only place "DB" is defined in this tab. */
const POSTGRES_GROUP = /postgres/i;

function isPostgresRule(rule: MonitoringRuleDto): boolean {
  return POSTGRES_GROUP.test(rule.group);
}

function toneOfSeverity(severity: MonitoringSeverity): FleetTone {
  switch (severity) {
    case "critical":
      return "crit";
    case "warning":
      return "warn";
    case "info":
      return "info";
    case "unknown":
      return "muted";
  }
}

function toneOfRuleState(state: MonitoringRuleDto["state"]): FleetTone {
  switch (state) {
    case "inactive":
      return "ok";
    case "pending":
      return "warn";
    case "firing":
      return "crit";
    case "unknown":
      return "muted";
  }
}

function toneOfVpsState(state: VpsState): FleetTone {
  switch (state) {
    case "HEALTHY":
      return "ok";
    case "WARNING":
      return "warn";
    case "CRITICAL":
    case "EMERGENCY":
      return "crit";
    case "UNKNOWN":
    case "MONITORING_BLIND":
      return "muted";
  }
}

/**
 * `NotMeasurable` and `FleetRow` (`FleetPrimitives.tsx`) have className-based
 * shared-kit equivalents now (`Dot`/`Row`, `ui/primitives.tsx`), but neither
 * carries a `data-testid`. These two locals reproduce the old grey "nicht
 * messbar" markup and row layout while keeping the testids this file's tests
 * assert on — the same call-site adaptation `LaunchView.tsx` uses for `Dot`.
 */
function DbNotMeasurable(props: { testId?: string }): ReactNode {
  const { t } = useLocale();
  return (
    <span className="mono muted" data-testid={props.testId}>
      {t("admin.gtcc.cockpit.infra.not_measurable")}
    </span>
  );
}

function DbRow(props: {
  tone: FleetTone;
  label: ReactNode;
  value: ReactNode;
  testId?: string;
}): ReactNode {
  return (
    <div data-testid={props.testId}>
      <Row tone={props.tone} value={props.value}>
        {props.label}
      </Row>
    </div>
  );
}

function DotCell(props: { tone: FleetTone; label: string; testId: string }): ReactNode {
  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: "var(--gt-sys-space-2)" }}
      data-testid={props.testId}
      data-tone={props.tone}
    >
      <Dot tone={props.tone} />
      {props.label}
    </span>
  );
}

function percent(value: number | null): ReactNode {
  return value === null ? <DbNotMeasurable /> : <Mono>{formatWholePercent(value)}</Mono>;
}

/* ------------------------------------------------------------ overview */

function OverviewPanel(props: { summary: MonitoringSummaryDto }): ReactNode {
  const { t } = useLocale();
  const overview = props.summary.overview;
  if (overview === undefined) {
    return <DbNotMeasurable testId="gtcc-dplane-mon-overview-unmeasured" />;
  }
  return (
    <>
      <DbRow
        tone={overview.targetsDown > 0 ? "warn" : "ok"}
        label={t("admin.gtcc.dplane.mon.targets")}
        value={
          <Mono testId="gtcc-dplane-mon-targets-value">
            {t("admin.gtcc.dplane.mon.targets.value", {
              up: overview.targetsUp,
              down: overview.targetsDown,
            })}
          </Mono>
        }
        testId="gtcc-dplane-mon-targets"
      />
      <DbRow
        tone={overview.criticalAlerts > 0 ? "crit" : overview.warningAlerts > 0 ? "warn" : "ok"}
        label={t("admin.gtcc.dplane.mon.active_alerts")}
        value={
          <Mono testId="gtcc-dplane-mon-active-alerts-value">
            {t("admin.gtcc.dplane.mon.active_alerts.value", {
              active: overview.activeAlerts,
              critical: overview.criticalAlerts,
              warning: overview.warningAlerts,
            })}
          </Mono>
        }
        testId="gtcc-dplane-mon-active-alerts"
      />
      <DbRow
        tone="muted"
        label={t("admin.gtcc.dplane.mon.last_checked")}
        value={<Mono testId="gtcc-dplane-mon-last-checked-value">{overview.lastCheckedAt}</Mono>}
        testId="gtcc-dplane-mon-last-checked"
      />
    </>
  );
}

/* --------------------------------------------------------------- rules */

function RulesTable(props: { rules: ReadonlyArray<MonitoringRuleDto> }): ReactNode {
  const { t } = useLocale();
  if (props.rules.length === 0) {
    return <DbNotMeasurable testId="gtcc-dplane-mon-rules-empty" />;
  }
  return (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE} data-testid="gtcc-dplane-mon-rules-table">
        <caption style={{ ...MONO_STYLE, textAlign: "start" }}>
          {t("admin.gtcc.dplane.mon.rules.caption", { count: props.rules.length })}
        </caption>
        <thead>
          <tr>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.rules.col.rule")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.rules.col.group")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.rules.col.severity")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.rules.col.state")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.rules.col.last_evaluation")}
            </th>
          </tr>
        </thead>
        <tbody>
          {props.rules.map((rule) => (
            <tr
              key={`${rule.group}/${rule.name}`}
              data-testid={`gtcc-dplane-mon-rule-${rule.name}`}
            >
              {/* `td` wie in der Referenz, kein Zeilenkopf. */}
              <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{rule.name}</td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{rule.group}</td>
              <td style={TABLE_CELL_STYLE}>
                <DotCell
                  tone={toneOfSeverity(rule.severity)}
                  label={t(`admin.gtcc.dplane.mon.severity.${rule.severity}`)}
                  testId={`gtcc-dplane-mon-rule-severity-${rule.name}`}
                />
              </td>
              <td style={TABLE_CELL_STYLE}>
                <DotCell
                  tone={toneOfRuleState(rule.state)}
                  label={t(`admin.gtcc.dplane.mon.rule_state.${rule.state}`)}
                  testId={`gtcc-dplane-mon-rule-state-${rule.name}`}
                />
              </td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                {rule.lastEvaluation ?? <DbNotMeasurable />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------- alerts */

function AlertsTable(props: { alerts: ReadonlyArray<MonitoringAlertDto> }): ReactNode {
  const { t } = useLocale();
  if (props.alerts.length === 0) {
    return <DbNotMeasurable testId="gtcc-dplane-mon-alerts-empty" />;
  }
  return (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE} data-testid="gtcc-dplane-mon-alerts-table">
        <caption style={{ ...MONO_STYLE, textAlign: "start" }}>
          {t("admin.gtcc.dplane.mon.alerts.caption", { count: props.alerts.length })}
        </caption>
        <thead>
          <tr>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.alerts.col.alert")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.alerts.col.host")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.alerts.col.severity")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.alerts.col.state")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.alerts.col.since")}
            </th>
          </tr>
        </thead>
        <tbody>
          {props.alerts.map((alert, index) => (
            <tr
              key={`${alert.alertname}-${alert.sanitizedHostLabel ?? index}`}
              data-testid={`gtcc-dplane-mon-alert-${alert.alertname}`}
            >
              <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{alert.alertname}</td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                {alert.sanitizedHostLabel ?? <DbNotMeasurable />}
              </td>
              <td style={TABLE_CELL_STYLE}>
                <DotCell
                  tone={toneOfSeverity(alert.severity)}
                  label={t(`admin.gtcc.dplane.mon.severity.${alert.severity}`)}
                  testId={`gtcc-dplane-mon-alert-severity-${alert.alertname}`}
                />
              </td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                {t(`admin.gtcc.dplane.mon.alert_state.${alert.state}`)}
              </td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                {alert.startsAt ?? <DbNotMeasurable />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ----------------------------------------------------------- vps fleet */

function FleetTable(props: { hosts: ReadonlyArray<VpsFleetHostDto> }): ReactNode {
  const { t } = useLocale();
  if (props.hosts.length === 0) {
    return <DbNotMeasurable testId="gtcc-dplane-mon-fleet-empty" />;
  }
  return (
    <div style={TABLE_WRAP_STYLE}>
      <table style={TABLE_STYLE} data-testid="gtcc-dplane-mon-fleet-table">
        <caption style={{ ...MONO_STYLE, textAlign: "start" }}>
          {t("admin.gtcc.dplane.mon.fleet.caption", { count: props.hosts.length })}
        </caption>
        <thead>
          <tr>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.host")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.role")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.state")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.cpu")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.memory")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.disk")}
            </th>
            <th scope="col" style={TABLE_HEAD_STYLE}>
              {t("admin.gtcc.dplane.mon.fleet.col.alerts")}
            </th>
          </tr>
        </thead>
        <tbody>
          {props.hosts.map((host) => (
            <tr key={host.host} data-testid={`gtcc-dplane-mon-fleet-${host.host}`}>
              <td style={{ ...TABLE_CELL_STYLE, ...HOST_STYLE }}>{host.host}</td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>{host.role}</td>
              <td style={TABLE_CELL_STYLE}>
                <DotCell
                  tone={toneOfVpsState(host.state)}
                  label={t(`admin.gtcc.dplane.mon.vps_state.${host.state}`)}
                  testId={`gtcc-dplane-mon-fleet-state-${host.host}`}
                />
              </td>
              <td style={TABLE_CELL_STYLE}>{percent(host.cpu?.busyPct ?? null)}</td>
              <td style={TABLE_CELL_STYLE}>{percent(host.memory?.availablePct ?? null)}</td>
              <td style={TABLE_CELL_STYLE}>{percent(host.disk?.usedPct ?? null)}</td>
              <td style={{ ...TABLE_CELL_STYLE, ...MONO_STYLE }}>
                {t("admin.gtcc.dplane.mon.fleet.alerts.value", {
                  critical: host.activeCritical,
                  warning: host.activeWarning,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ tab */

export function DbMonitoringTab(_props: DplaneTabProps): ReactNode {
  const { t } = useLocale();

  const summaryQuery = useQuery({
    queryKey: DPLANE_MONITORING_SUMMARY_KEY,
    queryFn: ({ signal }) => fetchDplaneMonitoringSummary(signal),
    ...DPLANE_QUERY_OPTIONS,
  });
  const fleetQuery = useQuery({
    queryKey: DPLANE_VPS_FLEET_KEY,
    queryFn: ({ signal }) => fetchDplaneVpsFleet(signal),
    ...DPLANE_QUERY_OPTIONS,
  });

  return (
    <div style={GRID_COLUMN_STYLE} data-testid="gtcc-dplane-mon-grid">
      <DplanePanel
        title={t("admin.gtcc.dplane.mon.overview.title")}
        note={t("admin.gtcc.dplane.mon.overview.note")}
        testId="gtcc-dplane-mon-overview"
      >
        <DplaneGate
          query={summaryQuery}
          testId="gtcc-dplane-mon-overview-panel"
          render={(summary: MonitoringSummaryDto) => <OverviewPanel summary={summary} />}
        />
      </DplanePanel>

      <DplanePanel
        title={t("admin.gtcc.dplane.mon.rules.title")}
        note={t("admin.gtcc.dplane.mon.rules.note")}
        testId="gtcc-dplane-mon-rules"
      >
        <DplaneGate
          query={summaryQuery}
          testId="gtcc-dplane-mon-rules-panel"
          render={(summary: MonitoringSummaryDto) => (
            <RulesTable rules={asList(summary.rules).filter(isPostgresRule)} />
          )}
        />
      </DplanePanel>

      <DplanePanel
        title={t("admin.gtcc.dplane.mon.alerts.title")}
        note={t("admin.gtcc.dplane.mon.alerts.note")}
        testId="gtcc-dplane-mon-alerts"
      >
        <DplaneGate
          query={summaryQuery}
          testId="gtcc-dplane-mon-alerts-panel"
          render={(summary: MonitoringSummaryDto) => {
            const dbRuleNames = new Set(
              asList(summary.rules)
                .filter(isPostgresRule)
                .map((rule) => rule.name),
            );
            return (
              <AlertsTable
                alerts={asList(summary.alerts).filter((alert) => dbRuleNames.has(alert.alertname))}
              />
            );
          }}
        />
      </DplanePanel>

      <DplanePanel title={t("admin.gtcc.dplane.mon.fleet.title")} testId="gtcc-dplane-mon-fleet">
        <DplaneGate
          query={fleetQuery}
          testId="gtcc-dplane-mon-fleet-panel"
          render={(fleet: VpsFleetDto) =>
            fleet.monitoringBlind === true ? (
              <DbRow
                tone="muted"
                label={t("admin.gtcc.dplane.mon.fleet.blind")}
                value={<DbNotMeasurable testId="gtcc-dplane-mon-fleet-blind-value" />}
                testId="gtcc-dplane-mon-fleet-blind"
              />
            ) : (
              <FleetTable hosts={asList(fleet.hosts)} />
            )
          }
        />
      </DplanePanel>
    </div>
  );
}
